/**
 * Centralized Accounts Payable — supplier bill model + status workflow.
 *
 * Sources:
 *  - rfq_award: timber/CNC quote awarded on the supplier RFQ pages
 *  - project_quote: Project Hub quote approved (incl. hardware & fasteners)
 *  - manual_upload: ad-hoc bill uploaded on the Billing & Payables page
 *  - purchase_order: ACH job PO issued from Purchasing or Other Quotes
 *
 * P&L note: supplierBills do NOT feed Project P&L directly — costs flow
 * through `qbExpenses` once a QuickBooks bill is linked (CRM auto-create on
 * ready-to-pay when qboBillWritesEnabled, or a manual link). Juliana pays in
 * QuickBooks; she no longer enters the bill when auto-create succeeds.
 *
 * Pure module — no firebase imports (unit-testable). Firestore/Storage
 * operations live in `supplierBillsService.ts`.
 */

import type { ProjectPLBucket } from '../types';
import type { PurchaseQbBillStatus } from '../types/purchasing';

export type SupplierBillSource = 'rfq_award' | 'project_quote' | 'manual_upload' | 'purchase_order';

export type SupplierBillStatus =
  | 'pending_review'
  | 'ready_to_pay'
  | 'entered_in_qb'
  | 'paid'
  | 'void';

export type SupplierBillCategory =
  | 'timber'
  | 'cnc'
  | 'fasteners'
  | 'hardware'
  | 'tooling'
  | 'freight'
  | 'other';

export const SUPPLIER_BILL_STATUS_LABELS: Record<SupplierBillStatus, string> = {
  pending_review: 'Pending review',
  ready_to_pay: 'Ready to pay',
  entered_in_qb: 'In QuickBooks',
  paid: 'Paid',
  void: 'Void',
};

export const SUPPLIER_BILL_CATEGORY_LABELS: Record<SupplierBillCategory, string> = {
  timber: 'Timber',
  cnc: 'CNC',
  fasteners: 'Fasteners',
  hardware: 'Hardware',
  tooling: 'Tooling',
  freight: 'Freight',
  other: 'Other',
};

export const SUPPLIER_BILL_SOURCE_LABELS: Record<SupplierBillSource, string> = {
  rfq_award: 'RFQ award',
  project_quote: 'Project Hub',
  manual_upload: 'Manual upload',
  purchase_order: 'Purchase order',
};

const PAYABLE_STEP_ORDER: SupplierBillStatus[] = [
  'pending_review',
  'ready_to_pay',
  'entered_in_qb',
  'paid',
];

/** One step earlier in the payable lifecycle, when that move is allowed. */
export function previousPayableStatus(status: SupplierBillStatus): SupplierBillStatus | null {
  const idx = PAYABLE_STEP_ORDER.indexOf(status);
  if (idx <= 0) return null;
  const prev = PAYABLE_STEP_ORDER[idx - 1];
  return (SUPPLIER_BILL_STATUS_FLOW[status] ?? []).includes(prev) ? prev : null;
}

/** Forward workflow, plus one step back (void allowed from any non-paid state). */
export const SUPPLIER_BILL_STATUS_FLOW: Record<SupplierBillStatus, SupplierBillStatus[]> = {
  pending_review: ['ready_to_pay', 'void'],
  ready_to_pay: ['entered_in_qb', 'pending_review', 'void'],
  entered_in_qb: ['paid', 'ready_to_pay', 'void'],
  paid: [],
  void: ['pending_review'],
};

export interface SupplierBillStatusEvent {
  status: SupplierBillStatus;
  byUserId: string;
  byUserName: string;
  at: Date;
  note?: string;
}

// ── Supplier payment terms ──────────────────────────────────────────────
// Entered by the approver when a quote is awarded. Each installment is a
// percent and/or a fixed dollar slice of the bill, anchored to an event;
// net days shift the payment after the anchor (e.g. "on completion, net 30").

export type SupplierPaymentTrigger =
  /** Due when the quote is approved (bill created). */
  | 'on_approval'
  /** Due when a linked Project Hub task finishes (e.g. "Timber lead time"). */
  | 'task_complete'
  /** Due on a specific calendar date. */
  | 'fixed_date';

export const SUPPLIER_PAYMENT_TRIGGER_LABELS: Record<SupplierPaymentTrigger, string> = {
  on_approval: 'On approval',
  task_complete: 'When task completes',
  fixed_date: 'Fixed date',
};

export interface SupplierPaymentInstallmentTerm {
  id: string;
  /** Display label, e.g. "50% down". */
  label?: string;
  /** Share of the bill total, 0–100. Slices should sum to 100 when no fixed amounts. */
  percent: number;
  /**
   * Fixed dollar slice entered in Payment terms. When set, cash-flow expansion
   * uses this instead of `percent × bill`. Last installment still absorbs rounding.
   */
  amount?: number;
  trigger: SupplierPaymentTrigger;
  /** Project Hub task anchoring `task_complete`. */
  taskId?: string;
  taskTitle?: string;
  /** Payment terms — days after the trigger event (net 30 → 30). */
  netDays?: number;
  /** Due date when trigger is `fixed_date`. */
  fixedDate?: Date;
  /** Statement, receipt, or extra quote for this stage. Not the bill's Spacebar file. */
  documents?: SupplierInstallmentDocument[];
}

export type SupplierInstallmentDocumentKind = 'statement' | 'invoice' | 'receipt' | 'quote' | 'other';

export interface SupplierInstallmentDocument {
  id: string;
  kind: SupplierInstallmentDocumentKind;
  fileName: string;
  storagePath: string;
  fileUrl: string;
  contentType?: string;
}

export interface SupplierPaymentTermsSpec {
  installments: SupplierPaymentInstallmentTerm[];
  /** Early payment discount, e.g. 1 (%) if paid within `earlyPayWithinDays`. */
  earlyPayDiscountPercent?: number;
  earlyPayWithinDays?: number;
  notes?: string;
  /**
   * Supplier absorbed freight in this timber/CNC quote.
   * Timber → leftover inbound freight off the cash line; CNC → leftover outbound.
   */
  includesFreight?: boolean;
}

/** True when the row has a usable percent or a fixed dollar amount. */
export function installmentHasShare(inst: SupplierPaymentInstallmentTerm): boolean {
  return inst.percent > 0 || (inst.amount != null && inst.amount > 0);
}

/** Dollars to the cent. Whole-dollar rounding was moving paid installments. */
export function roundMoney(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Amount typed in Mark paid. Blank, zero, and non-numeric values are rejected. */
export function parsePaidAmountInput(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  return roundMoney(n);
}

/** Dollar slice for one installment — fixed amount wins over percent. */
export function resolveInstallmentAmount(
  inst: SupplierPaymentInstallmentTerm,
  billAmount: number,
): number {
  if (inst.amount != null && inst.amount > 0) return roundMoney(inst.amount);
  return roundMoney(((Number(billAmount) || 0) * (Number(inst.percent) || 0)) / 100);
}

function roundPercent(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Write explicit dollars and percents that sum to the bill and to 100. */
export function stampInstallmentShares(
  rows: SupplierPaymentInstallmentTerm[],
  dollars: number[],
  billAmount: number,
  remainderIndex?: number,
): SupplierPaymentInstallmentTerm[] {
  const bill = roundMoney(billAmount);
  const percents = dollars.map((d) => (bill > 0 ? roundPercent((d / bill) * 100) : 0));
  const idx = remainderIndex != null && remainderIndex >= 0 && remainderIndex < percents.length
    ? remainderIndex
    : percents.length - 1;
  if (percents.length > 0 && bill > 0 && idx >= 0) {
    const others = percents.reduce((sum, n, i) => (i === idx ? sum : sum + n), 0);
    percents[idx] = roundPercent(100 - others);
  }
  return rows.map((row, i) => ({
    ...row,
    amount: dollars[i] > 0 ? roundMoney(dollars[i]) : undefined,
    percent: percents[i] > 0 ? percents[i] : 0,
  }));
}

function installmentIsLocked(
  row: { id: string },
  lockedIds?: ReadonlySet<string>,
): boolean {
  return !!lockedIds?.has(row.id);
}

/** Last row that can still absorb a dollar change. Locked rows are skipped. */
function flexibleBalanceIndex(
  rows: Array<{ id: string }>,
  editedIndex: number,
  lockedIds?: ReadonlySet<string>,
): number {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (i === editedIndex) continue;
    if (installmentIsLocked(rows[i], lockedIds)) continue;
    return i;
  }
  return -1;
}

/**
 * Installments Billing has approved for payment or marked paid. Their dollar
 * amounts stay put when the rest of the schedule is edited.
 */
export function releasedInstallmentIds(bill: {
  status?: string;
  paymentTerms?: { installments?: Array<{ id: string }> };
  installmentPayments?: Record<number, unknown>;
  installmentApprovals?: Record<number, unknown>;
}): string[] {
  const installments = bill.paymentTerms?.installments ?? [];
  if (bill.status === 'paid') return installments.map((row) => row.id);
  return installments
    .filter((_, idx) => !!bill.installmentPayments?.[idx] || !!bill.installmentApprovals?.[idx])
    .map((row) => row.id);
}

/** Installments already marked paid. Approved-but-unpaid rows are not included. */
export function paidInstallmentIds(bill: {
  status?: string;
  paymentTerms?: { installments?: Array<{ id: string }> };
  installmentPayments?: Record<number, unknown>;
}): string[] {
  const installments = bill.paymentTerms?.installments ?? [];
  if (bill.status === 'paid') return installments.map((row) => row.id);
  return installments
    .filter((_, idx) => !!bill.installmentPayments?.[idx])
    .map((row) => row.id);
}

/** True when a save would change or drop an approved or paid installment's dollars. */
export function releasedInstallmentAmountsChanged(
  previous: SupplierPaymentInstallmentTerm[],
  next: SupplierPaymentInstallmentTerm[],
  lockedIds: ReadonlySet<string>,
  billAmount: number,
): boolean {
  let changed = false;
  lockedIds.forEach((id) => {
    if (changed) return;
    const prev = previous.find((row) => row.id === id);
    const nxt = next.find((row) => row.id === id);
    if (!prev || !nxt) {
      changed = true;
      return;
    }
    if (resolveInstallmentAmount(prev, billAmount) !== resolveInstallmentAmount(nxt, billAmount)) {
      changed = true;
    }
  });
  return changed;
}

/**
 * Give the last row whatever dollars and percent are left so the set matches
 * the bill. Rows that would leave that remainder at or below zero are unchanged.
 */
export function normalizeInstallmentShares(
  rows: SupplierPaymentInstallmentTerm[],
  billAmount: number,
  lockedIds?: ReadonlySet<string>,
): SupplierPaymentInstallmentTerm[] {
  const bill = roundMoney(billAmount);
  if (bill <= 0 || rows.length === 0) return rows;
  const dollars = rows.map((row) => resolveInstallmentAmount(row, bill));
  const balanceIndex = flexibleBalanceIndex(rows, -1, lockedIds);
  if (balanceIndex < 0) return pinLockedAmounts(rows, rows, lockedIds, bill);
  const sumExcept = dollars.reduce((sum, n, i) => (i === balanceIndex ? sum : sum + n), 0);
  const remainder = roundMoney(bill - sumExcept);
  if (remainder <= 0) return pinLockedAmounts(rows, rows, lockedIds, bill);
  dollars[balanceIndex] = remainder;
  return pinLockedAmounts(stampInstallmentShares(rows, dollars, bill, balanceIndex), rows, lockedIds, bill);
}

/** Put each approved or paid installment back to the dollars it had before this edit. */
function pinLockedAmounts(
  rows: SupplierPaymentInstallmentTerm[],
  originals: SupplierPaymentInstallmentTerm[],
  lockedIds: ReadonlySet<string> | undefined,
  billAmount: number,
): SupplierPaymentInstallmentTerm[] {
  if (!lockedIds || lockedIds.size === 0) return rows;
  return rows.map((row) => {
    if (!lockedIds.has(row.id)) return row;
    const original = originals.find((item) => item.id === row.id);
    if (!original) return row;
    return { ...row, amount: resolveInstallmentAmount(original, billAmount) };
  });
}

/**
 * Dollars already typed on the schedule. Percent-only rows fall back to the
 * current bill so a raised invoice can replace that bill total.
 */
export function installmentScheduleTotal(
  rows: SupplierPaymentInstallmentTerm[],
  fallbackBill = 0,
): number {
  const explicit = roundMoney(rows.reduce(
    (sum, row) => sum + (row.amount != null && row.amount > 0 ? row.amount : 0),
    0,
  ));
  return explicit > 0 ? explicit : roundMoney(fallbackBill);
}

/**
 * The edited line no longer fits under the old bill because the other lines
 * are paid or already set. Keep those dollars and raise the bill to the new sum.
 */
function growBillAroundEdit(
  rows: SupplierPaymentInstallmentTerm[],
  dollars: number[],
  editedIndex: number,
  lockedIds: ReadonlySet<string> | undefined,
  originalBill: number,
): { ok: true; rows: SupplierPaymentInstallmentTerm[]; billAmount: number } {
  const nextBill = roundMoney(dollars.reduce((sum, n) => sum + n, 0));
  return {
    ok: true,
    billAmount: nextBill,
    rows: pinLockedAmounts(
      stampInstallmentShares(rows, dollars, nextBill, editedIndex),
      rows,
      lockedIds,
      originalBill,
    ),
  };
}

/**
 * Set the bill total. The last unpaid installment takes the difference.
 * Paid and approved amounts stay put, so the total cannot drop to their sum or below.
 */
export function applyBillTotalEdit(opts: {
  rows: SupplierPaymentInstallmentTerm[];
  currentBillAmount: number;
  nextBillAmount: number;
  lockedIds?: ReadonlySet<string>;
}): { ok: true; rows: SupplierPaymentInstallmentTerm[]; billAmount: number } | { ok: false } {
  const current = roundMoney(opts.currentBillAmount);
  const nextBill = roundMoney(opts.nextBillAmount);
  const { rows, lockedIds } = opts;
  if (nextBill <= 0 || rows.length === 0) return { ok: false };
  const balanceIndex = flexibleBalanceIndex(rows, -1, lockedIds);
  if (balanceIndex < 0) return { ok: false };
  const dollars = rows.map((row) => resolveInstallmentAmount(row, current));
  const others = roundMoney(dollars.reduce((sum, n, i) => (i === balanceIndex ? sum : sum + n), 0));
  const open = roundMoney(nextBill - others);
  if (open <= 0) return { ok: false };
  dollars[balanceIndex] = open;
  return {
    ok: true,
    billAmount: nextBill,
    rows: pinLockedAmounts(
      stampInstallmentShares(rows, dollars, nextBill, balanceIndex),
      rows,
      lockedIds,
      current,
    ),
  };
}

/**
 * Keep the edited row's dollars or percent, and put the difference on the
 * other end of the list (the last row, or the previous row when the last
 * one was edited). A dollar amount that no longer fits raises the bill total
 * instead of changing a paid installment. Rejects a value that would zero out
 * that balancing row.
 */
export function applyInstallmentEdit(opts: {
  rows: SupplierPaymentInstallmentTerm[];
  editedIndex: number;
  billAmount: number;
  mode: 'dollars' | 'percent';
  value: number;
  lockedIds?: ReadonlySet<string>;
}): { ok: true; rows: SupplierPaymentInstallmentTerm[]; billAmount?: number } | { ok: false } {
  const bill = roundMoney(opts.billAmount);
  const { rows, editedIndex, lockedIds } = opts;
  if (bill <= 0 || editedIndex < 0 || editedIndex >= rows.length) return { ok: false };
  if (installmentIsLocked(rows[editedIndex], lockedIds)) return { ok: false };
  const editedDollars = opts.mode === 'dollars'
    ? roundMoney(opts.value)
    : roundMoney((bill * opts.value) / 100);
  if (editedDollars <= 0) return { ok: false };
  if (rows.length === 1) {
    if (opts.mode === 'percent' && editedDollars !== bill) return { ok: false };
    const nextBill = opts.mode === 'dollars' ? editedDollars : bill;
    return {
      ok: true,
      billAmount: nextBill,
      rows: stampInstallmentShares(rows, [nextBill], nextBill),
    };
  }
  const balanceIndex = flexibleBalanceIndex(rows, editedIndex, lockedIds);
  const dollars = rows.map((row, i) => {
    if (installmentIsLocked(row, lockedIds)) return resolveInstallmentAmount(row, bill);
    return i === editedIndex ? editedDollars : resolveInstallmentAmount(row, bill);
  });
  if (balanceIndex < 0) {
    const current = resolveInstallmentAmount(rows[editedIndex], bill);
    // Paid lines can't shrink. A smaller unpaid amount becomes its own line,
    // and the rest of that installment stays on a new line with the same due terms.
    if (opts.mode === 'dollars' && editedDollars < current) {
      return splitInstallmentForPayment({
        rows,
        index: editedIndex,
        paymentAmount: editedDollars,
        billAmount: bill,
        newRowId: `term-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        lockedIds,
      });
    }
    const fixed = dollars.reduce((sum, n, i) => (i === editedIndex ? sum : sum + n), 0);
    if (roundMoney(editedDollars) !== roundMoney(bill - fixed)) {
      if (opts.mode === 'dollars' && editedDollars > roundMoney(bill - fixed)) {
        return growBillAroundEdit(rows, dollars, editedIndex, lockedIds, bill);
      }
      return { ok: false };
    }
    return {
      ok: true,
      rows: pinLockedAmounts(stampInstallmentShares(rows, dollars, bill, editedIndex), rows, lockedIds, bill),
    };
  }
  const lockedSum = dollars.reduce((sum, n, i) => (i === balanceIndex ? sum : sum + n), 0);
  const balanceDollars = roundMoney(bill - lockedSum);
  if (balanceDollars < 0) {
    return growBillAroundEdit(rows, dollars, editedIndex, lockedIds, bill);
  }
  if (balanceDollars === 0) {
    if (rows.length <= 1 || installmentIsLocked(rows[balanceIndex], lockedIds)) return { ok: false };
    const trimmedRows = rows.filter((_, i) => i !== balanceIndex);
    const trimmedDollars = dollars.filter((_, i) => i !== balanceIndex);
    const remainderIdx = trimmedRows.length - 1;
    return {
      ok: true,
      rows: pinLockedAmounts(
        stampInstallmentShares(trimmedRows, trimmedDollars, bill, remainderIdx),
        rows,
        lockedIds,
        bill,
      ),
    };
  }
  dollars[balanceIndex] = balanceDollars;
  return {
    ok: true,
    rows: pinLockedAmounts(stampInstallmentShares(rows, dollars, bill, balanceIndex), rows, lockedIds, bill),
  };
}

function installmentStageKey(inst: SupplierPaymentInstallmentTerm): string {
  if (inst.trigger === 'task_complete') return `task:${inst.taskId || ''}`;
  if (inst.trigger === 'fixed_date') {
    const d = inst.fixedDate;
    if (!d) return 'fixed:';
    return `fixed:${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }
  return 'on_approval';
}

function copyInstallmentStage(
  source: SupplierPaymentInstallmentTerm,
  id: string,
  amount: number,
): SupplierPaymentInstallmentTerm {
  return {
    id,
    percent: 0,
    amount,
    trigger: source.trigger,
    taskId: source.taskId,
    taskTitle: source.taskTitle,
    netDays: source.netDays,
    fixedDate: source.fixedDate ? new Date(source.fixedDate) : undefined,
  };
}

/**
 * Turn one stage into the statement amount plus a remainder row (same due
 * trigger). A following row that is already that remainder is updated
 * instead of inserting another one. Earlier rows stay put.
 */
export function splitInstallmentForPayment(opts: {
  rows: SupplierPaymentInstallmentTerm[];
  index: number;
  paymentAmount: number;
  billAmount: number;
  newRowId: string;
  lockedIds?: ReadonlySet<string>;
}): { ok: true; rows: SupplierPaymentInstallmentTerm[] } | { ok: false } {
  const bill = roundMoney(opts.billAmount);
  const { rows, index, lockedIds } = opts;
  if (bill <= 0 || index < 0 || index >= rows.length) return { ok: false };
  const source = rows[index];
  if (installmentIsLocked(source, lockedIds)) return { ok: false };
  const current = resolveInstallmentAmount(source, bill);
  const pay = roundMoney(opts.paymentAmount);
  if (pay <= 0 || pay >= current) return { ok: false };
  const remainder = roundMoney(current - pay);
  const next = rows[index + 1];
  const dollars = rows.map((row) => resolveInstallmentAmount(row, bill));
  dollars[index] = pay;
  let nextRows = rows.map((row) => ({ ...row }));
  const nextCanAbsorb = next
    && !installmentIsLocked(next, lockedIds)
    && installmentStageKey(next) === installmentStageKey(source);
  if (nextCanAbsorb) {
    dollars[index + 1] = resolveInstallmentAmount(next, bill) + remainder;
  } else {
    dollars.splice(index + 1, 0, remainder);
    nextRows = nextRows.slice();
    nextRows.splice(index + 1, 0, copyInstallmentStage(source, opts.newRowId, remainder));
  }
  nextRows[index] = { ...nextRows[index], amount: pay };
  return {
    ok: true,
    rows: pinLockedAmounts(
      stampInstallmentShares(nextRows, dollars, bill, index + 1),
      rows,
      lockedIds,
      bill,
    ),
  };
}

/** Move paid/approved records onto the installment's new index after a split. */
export function reindexInstallmentRecords<T>(
  previous: Array<{ id: string }>,
  next: Array<{ id: string }>,
  records: Record<number, T> | undefined,
): Record<number, T> {
  if (!records) return {};
  const nextIndexById = new Map(next.map((row, i) => [row.id, i]));
  const out: Record<number, T> = {};
  previous.forEach((row, oldIdx) => {
    const rec = records[oldIdx];
    if (!rec) return;
    const newIdx = nextIndexById.get(row.id);
    if (newIdx == null) return;
    out[newIdx] = rec;
  });
  return out;
}

/** When a bill total is known, dollars must match it and percents must total 100. */
export function validateSupplierPaymentTerms(
  terms: SupplierPaymentTermsSpec,
  billAmount?: number,
): { ok: true } | { ok: false; message: string } {
  if (!terms.installments.length) {
    return { ok: false, message: 'Add at least one payment installment.' };
  }
  for (const inst of terms.installments) {
    if (!installmentHasShare(inst)) {
      return { ok: false, message: 'Every installment needs a percent or dollar amount greater than zero.' };
    }
    if (inst.trigger === 'task_complete' && !inst.taskId) {
      return { ok: false, message: 'Pick the task that triggers each task-based payment.' };
    }
    if (inst.trigger === 'fixed_date' && !inst.fixedDate) {
      return { ok: false, message: 'Pick a date for each fixed-date payment.' };
    }
  }
  const totalPct = terms.installments.reduce((s, i) => s + (Number(i.percent) || 0), 0);
  if (billAmount != null && billAmount > 0) {
    const totalAmt = terms.installments.reduce(
      (s, i) => s + resolveInstallmentAmount(i, billAmount),
      0,
    );
    const bill = roundMoney(billAmount);
    if (Math.abs(roundMoney(totalAmt) - bill) > 0.009) {
      return {
        ok: false,
        message: `Installments must total $${bill.toLocaleString('en-US')} (currently $${roundMoney(totalAmt).toLocaleString('en-US')}).`,
      };
    }
    if (Math.abs(totalPct - 100) > 0.5) {
      return { ok: false, message: `Installments must total 100% (currently ${totalPct.toFixed(1)}%).` };
    }
    return { ok: true };
  }
  if (Math.abs(totalPct - 100) > 0.5) {
    return { ok: false, message: `Installments must total 100% (currently ${totalPct.toFixed(1)}%).` };
  }
  return { ok: true };
}

/** One expected payment for a bill — the AP side of the cash flow projection. */
export interface SupplierBillPaymentSlice {
  billId?: string;
  vendorName: string;
  label: string;
  amount: number;
  dueDate: Date | null;
  /** True when the due date is unresolved (missing task / no anchor). */
  estimated: boolean;
  /** Position in `paymentTerms.installments` — joins per-installment payments. */
  installmentIndex?: number;
}

/** Record of one installment actually paid (keyed by installment index on the bill). */
export interface SupplierInstallmentPayment {
  paidAt: Date;
  byUserId: string;
  byUserName: string;
  note?: string;
  /** Cash recorded for this payment. Absent on older rows — those fall back to the scheduled slice. */
  amountPaid?: number;
}

/** Keys are installment indexes into `paymentTerms.installments`. */
export type SupplierInstallmentPayments = Record<number, SupplierInstallmentPayment>;

/** Admin/CFO sign-off that one installment may be paid (keyed by installment index). */
export interface SupplierInstallmentApproval {
  approvedAt: Date;
  byUserId: string;
  byUserName: string;
  note?: string;
}

/** Keys are installment indexes into `paymentTerms.installments`. */
export type SupplierInstallmentApprovals = Record<number, SupplierInstallmentApproval>;

/**
 * Multi-installment bills release payments one at a time: each installment
 * needs an admin/CFO approval before it can be marked paid. Single-installment
 * bills are covered by the bill-level "Ready to be paid" step.
 */
export function installmentApprovalRequired(
  terms: SupplierPaymentTermsSpec | undefined,
): boolean {
  return (terms?.installments.length ?? 0) >= 2;
}

export function areAllInstallmentsPaid(
  terms: SupplierPaymentTermsSpec,
  payments: SupplierInstallmentPayments | undefined,
): boolean {
  if (!terms.installments.length) return false;
  return terms.installments.every((_, idx) => !!payments?.[idx]);
}

/** Who/when the bill was marked paid at the workflow level, if ever. */
export function latestBillPaidEvent(
  bill: Pick<SupplierBill, 'statusHistory'>,
): SupplierBillStatusEvent | undefined {
  const history = bill.statusHistory || [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const event = history[i];
    if (event?.status === 'paid') return event;
  }
  return undefined;
}

/** Paid-at for clearing lag — bill-level event, else latest installment payment. */
export function supplierBillPaidAt(
  bill: Pick<SupplierBill, 'statusHistory' | 'installmentPayments'>,
): Date | null {
  const event = latestBillPaidEvent(bill);
  if (event?.at && !Number.isNaN(event.at.getTime())) return event.at;
  const payments = bill.installmentPayments;
  if (!payments) return null;
  let latest: Date | null = null;
  for (const p of Object.values(payments)) {
    if (!p?.paidAt || Number.isNaN(p.paidAt.getTime())) continue;
    if (!latest || p.paidAt.getTime() > latest.getTime()) latest = p.paidAt;
  }
  return latest;
}

/**
 * Dollars already paid on a bill: the full amount once the bill is `paid`,
 * otherwise the sum of installments individually marked paid. Keeps AP totals
 * honest while a multi-installment bill is partially paid.
 */
export function supplierBillPaidAmount(bill: SupplierBill): number {
  const total = roundMoney(Number(bill.amount) || 0);
  if (bill.status === 'void') return 0;
  if (bill.status === 'paid') return total;
  if (!bill.paymentTerms?.installments?.length || !bill.installmentPayments) return 0;
  return expandSupplierBillPayments(bill)
    .filter(
      (s) => s.installmentIndex !== undefined && !!bill.installmentPayments?.[s.installmentIndex],
    )
    .reduce((sum, s) => sum + s.amount, 0);
}

/** First installment without a payment record — null when everything is paid. */
export function nextUnpaidInstallmentIndex(
  terms: SupplierPaymentTermsSpec,
  payments: SupplierInstallmentPayments | undefined,
): number | null {
  const idx = terms.installments.findIndex((_, i) => !payments?.[i]);
  return idx === -1 ? null : idx;
}

function addCalendarDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + Math.max(0, Math.round(days)));
  return d;
}

function calendarDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function installmentAnchorDate(
  bill: Pick<SupplierBill, 'createdAt'>,
  inst: SupplierPaymentInstallmentTerm,
  taskEndDate?: Date | null,
): Date | null {
  if (inst.trigger === 'on_approval') return bill.createdAt ?? null;
  if (inst.trigger === 'task_complete') return taskEndDate ?? null;
  return inst.fixedDate ?? null;
}

function scheduledSliceAmount(bill: SupplierBill, installmentIndex: number | null | undefined): number {
  if (installmentIndex == null || !bill.paymentTerms?.installments.length) {
    return roundMoney(bill.amount);
  }
  const slice = expandSupplierBillPayments(bill).find((s) => s.installmentIndex === installmentIndex);
  return roundMoney(slice?.amount ?? 0);
}

export interface EarlyPayAmountSuggestion {
  scheduledAmount: number;
  /** Prefill. The discount only when the pay date is inside the window. */
  suggestedAmount: number;
  /** Scheduled slice times (1 - percent/100), when early-pay terms exist. */
  discountedAmount: number | null;
  earlyPayApplies: boolean;
  /** Terms exist, but the trigger date is unknown — do not prefill the discount. */
  windowUndated: boolean;
  earlyPayDiscountPercent?: number;
  earlyPayWithinDays?: number;
}

/**
 * Suggest the cash amount for the payment being marked. The window is the
 * installment's trigger anchor plus `earlyPayWithinDays`, before net days.
 * Later installments are not recalculated.
 */
export function suggestEarlyPayAmount(args: {
  bill: SupplierBill;
  installmentIndex?: number | null;
  paidAt: Date;
  taskEndDate?: Date | null;
}): EarlyPayAmountSuggestion {
  const scheduledAmount = scheduledSliceAmount(args.bill, args.installmentIndex);
  const percent = args.bill.paymentTerms?.earlyPayDiscountPercent;
  const withinDays = args.bill.paymentTerms?.earlyPayWithinDays;
  const hasEarlyPay = !!percent && percent > 0 && !!withinDays && withinDays > 0;
  if (!hasEarlyPay) {
    return {
      scheduledAmount,
      suggestedAmount: scheduledAmount,
      discountedAmount: null,
      earlyPayApplies: false,
      windowUndated: false,
    };
  }

  const discountedAmount = roundMoney(scheduledAmount * (1 - percent / 100));
  const inst = args.installmentIndex != null
    ? args.bill.paymentTerms?.installments[args.installmentIndex]
    : undefined;
  const anchor = inst ? installmentAnchorDate(args.bill, inst, args.taskEndDate) : null;
  if (!inst || !anchor) {
    return {
      scheduledAmount,
      suggestedAmount: scheduledAmount,
      discountedAmount,
      earlyPayApplies: false,
      windowUndated: true,
      earlyPayDiscountPercent: percent,
      earlyPayWithinDays: withinDays,
    };
  }

  const deadline = addCalendarDays(anchor, withinDays);
  const earlyPayApplies = calendarDay(args.paidAt) <= calendarDay(deadline);
  return {
    scheduledAmount,
    suggestedAmount: earlyPayApplies ? discountedAmount : scheduledAmount,
    discountedAmount,
    earlyPayApplies,
    windowUndated: false,
    earlyPayDiscountPercent: percent,
    earlyPayWithinDays: withinDays,
  };
}

/**
 * Cash actually recorded on a bill. Paid rows without `amountPaid` use the
 * scheduled slice. A paid bill with no terms uses `amountPaid` or the bill
 * total. Unpaid installments contribute nothing, so a discount on one
 * payment does not change the others.
 */
export function recordedSupplierBillCashPaid(bill: SupplierBill): number {
  if (bill.status === 'void') return 0;
  const terms = bill.paymentTerms;
  if (terms?.installments.length) {
    const slices = expandSupplierBillPayments(bill).filter((s) => s.installmentIndex !== undefined);
    if (slices.length === 0) {
      return bill.status === 'paid' ? roundMoney(bill.amountPaid ?? bill.amount) : 0;
    }
    let sum = 0;
    let any = false;
    for (const slice of slices) {
      const payment = bill.installmentPayments?.[slice.installmentIndex!];
      if (payment) {
        any = true;
        sum += payment.amountPaid != null && Number.isFinite(payment.amountPaid)
          ? roundMoney(payment.amountPaid)
          : slice.amount;
      } else if (bill.status === 'paid') {
        any = true;
        sum += slice.amount;
      }
    }
    return any ? roundMoney(sum) : 0;
  }
  if (bill.status !== 'paid') return 0;
  if (bill.amountPaid != null && Number.isFinite(bill.amountPaid)) return roundMoney(bill.amountPaid);
  return roundMoney(bill.amount);
}

/** Purchase-order close total: sum of cash recorded on every non-void bill. */
export function supplierBillsPurchasedAmount(bills: SupplierBill[]): number {
  return roundMoney(bills.reduce((sum, bill) => sum + recordedSupplierBillCashPaid(bill), 0));
}

/** Editor default for a single 100% row — leftover after a bill is split. */
const GENERIC_FULL_PAYMENT_LABEL = /^full payment\b/i;

/** List label for one installment. Drops stale "Full payment" once there are 2+. */
export function supplierInstallmentSliceLabel(
  inst: SupplierPaymentInstallmentTerm,
  index: number,
  installmentCount: number,
): string {
  const raw = inst.label?.trim() ?? '';
  if (installmentCount <= 1) return raw || 'Full payment';
  if (!raw || GENERIC_FULL_PAYMENT_LABEL.test(raw)) {
    if (inst.amount != null && inst.amount > 0) {
      return `${inst.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })} installment`;
    }
    return `${Math.round(inst.percent)}% installment`;
  }
  return raw;
}

/**
 * Expand a bill into expected payments. Bills without terms fall back to a
 * single payment at `dueDate`. Task-anchored slices resolve through
 * `taskEndDateById` (live schedule), so payments move with the plan.
 * Early-pay discounts are ignored here — the projection assumes full price.
 */
export function expandSupplierBillPayments(
  bill: SupplierBill,
  ctx?: { taskEndDateById?: Map<string, Date> },
): SupplierBillPaymentSlice[] {
  const total = roundMoney(Number(bill.amount) || 0);
  const terms = bill.paymentTerms;
  if (!terms || terms.installments.length === 0) {
    return [
      {
        billId: bill.id,
        vendorName: bill.vendorName,
        label: bill.description || 'Supplier bill',
        amount: total,
        dueDate: bill.dueDate ?? null,
        estimated: !bill.dueDate,
      },
    ];
  }

  const slices: SupplierBillPaymentSlice[] = [];
  let allocated = 0;
  terms.installments.forEach((inst, idx) => {
    const isLast = idx === terms.installments.length - 1;
    const amount = isLast
      ? total - allocated
      : resolveInstallmentAmount(inst, total);
    allocated += amount;

    let anchor: Date | null = null;
    let estimated = false;
    if (inst.trigger === 'on_approval') {
      anchor = bill.createdAt ?? null;
    } else if (inst.trigger === 'task_complete') {
      anchor = (inst.taskId && ctx?.taskEndDateById?.get(inst.taskId)) || null;
      if (!anchor) estimated = true;
    } else {
      anchor = inst.fixedDate ?? null;
      if (!anchor) estimated = true;
    }

    const dueDate =
      anchor && inst.trigger !== 'fixed_date'
        ? addCalendarDays(anchor, inst.netDays ?? 0)
        : anchor;

    slices.push({
      billId: bill.id,
      vendorName: bill.vendorName,
      label: supplierInstallmentSliceLabel(inst, idx, terms.installments.length),
      amount,
      dueDate,
      estimated,
      installmentIndex: idx,
    });
  });
  return slices.filter(s => s.amount > 0);
}

const fmtScheduleDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const fmtScheduleMoney = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Trigger phrasing shared by schedule text and UI, e.g. "net 30 after Timber lead time". */
export function describeSupplierInstallmentTrigger(inst: SupplierPaymentInstallmentTerm): string {
  const net = inst.netDays ? `net ${inst.netDays} ` : '';
  if (inst.trigger === 'on_approval') return `${net}on approval`.trim();
  if (inst.trigger === 'task_complete') return `${net}after ${inst.taskTitle || 'task'}`;
  return inst.fixedDate ? `on ${fmtScheduleDate(inst.fixedDate)}` : 'on a fixed date (not set)';
}

/**
 * Full payment schedule as multi-line text for hover titles — what to pay,
 * when, and what's already been paid. One line per installment:
 * "Payment 1 of 2 — $15,412 (50%) — on approval — due Sep 3, 2026 — PAID Aug 20 by Juliana".
 */
export function describeSupplierBillPaymentSchedule(
  bill: SupplierBill,
  slices: SupplierBillPaymentSlice[],
): string {
  const terms = bill.paymentTerms;
  if (!terms || !terms.installments.length) {
    return bill.dueDate
      ? `Single payment of ${fmtScheduleMoney(bill.amount)} due ${fmtScheduleDate(bill.dueDate)}`
      : `Single payment of ${fmtScheduleMoney(bill.amount)} — no due date set`;
  }

  const termSlices = slices.filter(s => s.installmentIndex !== undefined);
  const approvalRequired = installmentApprovalRequired(terms);
  const lines = termSlices.map((slice, pos) => {
    const idx = slice.installmentIndex!;
    const inst = terms.installments[idx];
    const paid = bill.installmentPayments?.[idx];
    const approval = bill.installmentApprovals?.[idx];
    const billPaid = latestBillPaidEvent(bill);
    const parts = [
      `Payment ${pos + 1} of ${termSlices.length} — ${fmtScheduleMoney(slice.amount)} (${Math.round(inst?.percent ?? 0)}%)`,
      inst ? describeSupplierInstallmentTrigger(inst) : slice.label,
      slice.dueDate
        ? `due ${fmtScheduleDate(slice.dueDate)}${inst?.trigger === 'task_complete' ? ' (tracks schedule)' : ''}`
        : 'due date TBD',
    ];
    if (paid) {
      const amountNote =
        paid.amountPaid != null && roundMoney(paid.amountPaid) !== roundMoney(slice.amount)
          ? ` ${paid.amountPaid.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
          : '';
      parts.push(`PAID ${fmtScheduleDate(paid.paidAt)}${amountNote} by ${paid.byUserName}`);
    } else if (bill.status === 'paid') {
      parts.push(
        billPaid
          ? `PAID ${fmtScheduleDate(billPaid.at)} by ${billPaid.byUserName}`
          : 'PAID',
      );
    } else if (!approvalRequired) {
      parts.push('not yet paid');
    } else if (approval) {
      parts.push(
        `APPROVED ${fmtScheduleDate(approval.approvedAt)} by ${approval.byUserName} — not yet paid`,
      );
    } else {
      parts.push('awaiting approval');
    }
    return parts.join(' — ');
  });

  if (terms.earlyPayDiscountPercent && terms.earlyPayWithinDays) {
    lines.push(
      `${terms.earlyPayDiscountPercent}% early-pay discount when paid within ${terms.earlyPayWithinDays} days`,
    );
  }
  if (terms.notes?.trim()) lines.push(`Notes: ${terms.notes.trim()}`);
  return lines.join('\n');
}

/** Short human summary for list rows, e.g. "50% on approval · 50% net 30 after Timber lead time". */
export function describeSupplierPaymentTerms(terms: SupplierPaymentTermsSpec): string {
  const parts = terms.installments.map(inst => {
    const share =
      inst.amount != null && inst.amount > 0
        ? inst.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : `${Math.round(inst.percent)}%`;
    const net = inst.netDays ? ` net ${inst.netDays}` : '';
    if (inst.trigger === 'on_approval') return `${share} on approval${net}`;
    if (inst.trigger === 'task_complete') {
      return `${share}${net} after ${inst.taskTitle || 'task'}`;
    }
    return `${share} on ${inst.fixedDate ? inst.fixedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'date'}`;
  });
  const discount =
    terms.earlyPayDiscountPercent && terms.earlyPayWithinDays
      ? ` · ${terms.earlyPayDiscountPercent}% discount within ${terms.earlyPayWithinDays}d`
      : '';
  return parts.join(' · ') + discount;
}

export interface SupplierBill {
  id?: string;
  source: SupplierBillSource;
  category: SupplierBillCategory;
  vendorName: string;
  supplierOrgId?: string;
  rfqProjectId?: string;
  rfqSubmissionId?: string;
  purchaseRequisitionId?: string;
  poNumber?: string;
  projectFileId?: string;
  dealId?: string;
  projectId?: string;
  plBucket?: ProjectPLBucket;
  description: string;
  amount: number;
  dueDate?: Date;
  fileUrl?: string;
  storagePath?: string;
  fileName?: string;
  status: SupplierBillStatus;
  /** EA's QuickBooks reference (bill number / memo) once entered. */
  qbReferenceNote?: string;
  /** Auto-created (or attempted) QuickBooks vendor bill for a designer-invoice PO. */
  qbBillStatus?: PurchaseQbBillStatus;
  qbBillId?: string;
  qbBillUrl?: string;
  qbBillMessage?: string;
  /** Payment breakdown entered at quote approval; drives AP cash timing. */
  paymentTerms?: SupplierPaymentTermsSpec;
  /**
   * Prior paymentTerms snapshots when terms are replaced (CNC overwrite recovery).
   * Written by setSupplierBillPaymentTerms; not always loaded into the client model.
   */
  termsHistory?: Array<Record<string, unknown>>;
  /** Installments actually paid, keyed by index into `paymentTerms.installments`. */
  installmentPayments?: SupplierInstallmentPayments;
  /**
   * Cash recorded when a bill with no installment terms is marked paid.
   * Installment bills store this on each payment instead.
   */
  amountPaid?: number;
  /** Admin/CFO approvals releasing each installment for payment (multi-installment bills). */
  installmentApprovals?: SupplierInstallmentApprovals;
  /**
   * How the office paid the bill (ACH / check / card). Drives Forecast clearing
   * lag until QuickBooks shows the bank clear.
   */
  paymentMethod?: string | null;
  /**
   * When QuickBooks BillPayment cleared the bank (synced from qbBillPayments).
   * Until set (or lag elapses), paid bills stay on the cash forecast as clearing.
   */
  qbClearedAt?: Date | null;
  qbPaymentDepositDate?: Date | null;
  statusHistory: SupplierBillStatusEvent[];
  createdByUserId: string;
  createdByUserName: string;
  createdAt: Date;
  updatedAt: Date;
}

export function canTransitionSupplierBill(
  from: SupplierBillStatus,
  to: SupplierBillStatus,
): boolean {
  return SUPPLIER_BILL_STATUS_FLOW[from]?.includes(to) ?? false;
}

function cncAwardBillDedupeKey(bill: SupplierBill): string | null {
  if (bill.category !== 'cnc' || bill.status === 'void' || !bill.rfqProjectId || !bill.supplierOrgId) {
    return null;
  }
  const submissionId = (bill.rfqSubmissionId || '').trim();
  // Structure-split awards are one bill per quote. Only legacy org-level keys collapse together.
  if (submissionId && !submissionId.startsWith('org:')) {
    return `${bill.rfqProjectId}:sub:${submissionId}`;
  }
  return `${bill.rfqProjectId}:org:${bill.supplierOrgId}`;
}

function preferCncAwardBill(a: SupplierBill, b: SupplierBill): SupplierBill {
  const aTerms = a.paymentTerms?.installments?.length ?? 0;
  const bTerms = b.paymentTerms?.installments?.length ?? 0;
  if (aTerms !== bTerms) return bTerms > aTerms ? b : a;
  const aDated = a.dueDate ? 1 : 0;
  const bDated = b.dueDate ? 1 : 0;
  if (aDated !== bDated) return bDated ? b : a;
  const aT = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
  const bT = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
  return bT >= aT ? b : a;
}

/**
 * Drop duplicate CNC award bills for the same quote. A structure-split award
 * keeps one bill per submission. An org-level bill (`org:{id}`) is only folded
 * into a submission bill when it is the same dollar amount — the old "terms
 * copy" of one award — not when it is a different structure's price.
 */
export function collapseDuplicateCncAwardBills(bills: SupplierBill[]): SupplierBill[] {
  const winnerByKey = new Map<string, SupplierBill>();
  for (const bill of bills) {
    const key = cncAwardBillDedupeKey(bill);
    if (!key) continue;
    const current = winnerByKey.get(key);
    winnerByKey.set(key, current ? preferCncAwardBill(current, bill) : bill);
  }
  const kept = bills.filter((bill) => {
    const key = cncAwardBillDedupeKey(bill);
    if (!key) return true;
    return winnerByKey.get(key) === bill;
  });
  const submissionAmounts = new Set<string>();
  for (const bill of kept) {
    if (bill.category !== 'cnc' || !bill.rfqProjectId || !bill.supplierOrgId) continue;
    const submissionId = (bill.rfqSubmissionId || '').trim();
    if (!submissionId || submissionId.startsWith('org:')) continue;
    submissionAmounts.add(
      `${bill.rfqProjectId}:${bill.supplierOrgId}:${Math.round(Number(bill.amount) || 0)}`,
    );
  }
  return kept.filter((bill) => {
    if (bill.category !== 'cnc' || !bill.rfqProjectId || !bill.supplierOrgId) return true;
    const submissionId = (bill.rfqSubmissionId || '').trim();
    if (submissionId && !submissionId.startsWith('org:')) return true;
    const amountKey = `${bill.rfqProjectId}:${bill.supplierOrgId}:${Math.round(Number(bill.amount) || 0)}`;
    return !submissionAmounts.has(amountKey);
  });
}
